import {
  useState,
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useRef,
} from "react";
import { styled } from "@stitches/react";
import {
  Input,
  Button,
  SelectionList,
  RemovableTag,
  SelectionListStructuredValue,
} from "@/vendor/Forge/dist";
import SearchIcon from "@/components/Icons/search";
import { Filter, Filters } from "@/types/order-table";
import { Metrics } from "@/contexts/metrics-context";
import { Orders } from "@/contexts/orders-context";
import { useLocation } from "react-router-dom";

const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const Content = styled("div", {
  display: "flex",
});

const CTA = styled("div", {
  display: "flex",
  flexDirection: "row-reverse",
});

const LeftPanel = styled("div", {
  width: "50%",
  borderRight: "1px solid  #B6B7B7",
  maxHeight: 320,
  overflowY: "scroll",
});

const Search = styled("div", {
  display: "flex",
  gap: 16,
  marginBottom: 16,

  "& svg": {
    position: "absolute",
    top: 8,
    left: 8,
  },
  "& > button": {
    width: 160,
  },
});

const RightPanel = styled("div", {
  width: "50%",
  paddingLeft: 8,
  maxHeight: 320,
  overflowY: "scroll",
});

const Summary = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,

  "& > span": {
    fontWeight: "bold",
  },
});

const Tags = styled("div", {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});

const setAllValuesTo = (
  originalList: string[],
  value: boolean,
  prev?: SelectionListStructuredValue,
): SelectionListStructuredValue => {
  const reduced = originalList?.reduce((acc, curr) => {
    return { ...acc, [curr]: value };
  }, {});
  return { ...prev, ...reduced };
};

const filtersMap: {
  [key: string]: Filter;
} = {
  insurance: "insurances",
  provider: "providerNames",
  department: "departments",
  authStatus: "authStatus",
  orderName: "orderNames",
  patientName: "patientNames",
  assignedUser: "assignedUser",
  clinicalFacilityDetails: "clinicalFacilityDetails",
  assignedUsers: "assignedUsers",
  secondaryInsurance: "secondaryInsurances",
  status: "statusList",
};

export const setInitialMasterList = (
  originalList: string[],
  type: Filter,
  activeFilters: Filters,
) => {
  const activeFiltersAsSet = new Set([
    ...(activeFilters?.[filtersMap[type]] ?? []),
  ]);
  if (!activeFiltersAsSet.size) return setAllValuesTo(originalList, false);
  return originalList.reduce(
    (acc, curr) => ({ ...acc, [curr]: activeFiltersAsSet.has(curr) }),
    {},
  );
};

const getSelectedList = (masterList: SelectionListStructuredValue) => {
  return Object.keys(masterList).filter((key) => masterList[key]);
};

const convertListToSelectionListStructuredValue = (
  masterList: SelectionListStructuredValue,
) => {
  return getSelectedList(masterList).reduce(
    (acc, curr) => ({ ...acc, [curr]: true }),
    {},
  );
};

interface Props {
  type: Filter;
  originalList: string[];
  useContext: () => Metrics | Orders;
}

const AdvanceFilter = ({ type, originalList, useContext }: Props) => {
  const { filters: activeFilters, updateFilters } = useContext();
  const [searchKey, setSearchKey] = useState("");
  const [masterList, setMasterList] = useState<SelectionListStructuredValue>(
    setInitialMasterList(originalList, type, activeFilters),
  );
  const [filteredList, setFilteredList] = useState<string[]>(originalList);
  //const [selectedItemsPersistent, setSelectedItemsPersistent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedItems = getSelectedList(masterList);
  const { pathname } = useLocation();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (pathname.includes("user-activity")) {
      setMasterList(
        setInitialMasterList(originalList, "assignedUsers", activeFilters),
      );
    }
  }, [originalList, activeFilters, pathname, type]);

  const handleItemClick = (
    _:
      | React.MouseEvent<HTMLUListElement, MouseEvent>
      | React.KeyboardEvent<HTMLUListElement>,
    key: string | null,
  ) => {
    if (key === null) return;
    setMasterList({
      ...masterList,
      [key]: !masterList[key],
    });
  };

  const handleClearAllClick = () => {
    setMasterList(setAllValuesTo(originalList, false));
    setFilteredList(originalList);
    setSearchKey("");
  };
  const handleSelectAllClick = () => {
    if (searchKey !== "") {
      setFilteredList(
        originalList.filter((l) =>
          l.toLowerCase().includes(searchKey.toLocaleLowerCase()),
        ),
      );
    }
    setMasterList((prev) => setAllValuesTo(filteredList, true, prev));
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const key = e.target.value;
    setSearchKey(key);
    setFilteredList(
      originalList.filter((l) =>
        l.toLowerCase().includes(key.toLocaleLowerCase()),
      ),
    );
  };

  const handleRemoveTag = (key: string) => {
    setMasterList({
      ...masterList,
      [key]: !masterList[key],
    });
  };
  const processFilters = (filtersMap: Filter, selectedItems: string[]) => {
    if (selectedItems.length === originalList.length) {
      updateFilters(filtersMap, []);
    } else {
      updateFilters(filtersMap, selectedItems);
    }
  };
  const handleFilterResultsClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (pathname.includes("user-activity")) {
      processFilters(filtersMap["assignedUsers"], selectedItems);
      const closeButton = document.querySelector(
        'button[aria-label="Close Modal"]',
      );
      if (closeButton) closeButton.id = "closeModal";
      const modalClose = document.getElementById("closeModal");
      if (modalClose) modalClose.click();
    } else {
      processFilters(filtersMap[type], selectedItems);
      const closeButton = document.querySelector(
        'button[aria-label="Close Modal"]',
      );
      if (closeButton) closeButton.id = "closeModal";
      const modalClose = document.getElementById("closeModal");
      if (modalClose) modalClose.click();
    }
  };

  return (
    <Wrapper>
      <Content>
        <LeftPanel>
          <Search>
            <Input
              placeholder="Search"
              icon={SearchIcon}
              onChange={handleSearch}
              value={searchKey}
              ref={inputRef}
            />
            <Button variant="tertiary" onClick={handleSelectAllClick}>
              Select All
            </Button>
          </Search>
          <SelectionList
            options={filteredList}
            onClickItem={handleItemClick}
            value={convertListToSelectionListStructuredValue(masterList)}
          />
        </LeftPanel>
        <RightPanel>
          <Summary>
            <span>Selected({selectedItems.length})</span>
            <Button variant="tertiary" onClick={handleClearAllClick}>
              Clear All
            </Button>
          </Summary>
          <Tags>
            {selectedItems.map((selectedItem) => (
              <RemovableTag
                key={selectedItem}
                text={selectedItem}
                onRemove={() => handleRemoveTag(selectedItem)}
              />
            ))}
          </Tags>
        </RightPanel>
      </Content>
      <CTA>
        <Button onClick={handleFilterResultsClick}>Filter Results</Button>
      </CTA>
    </Wrapper>
  );
};

export default AdvanceFilter;
